import { c as _c } from "react/compiler-runtime";
import { capitalize, Divider, Popover, Stack, Typography } from '@mui/material';
import { ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';
import { OPERATORS } from '@/components/conditions/OperatorSelect';
import { ConditionPanelTriggerButton, MultiSelectConditionPanel } from '@/components/conditions/conditionComponents';
import { FilterOperatorSelect } from '@/components/conditions/filterOperatorSelect';
import { useConditionPanel } from '@/components/conditions/useConditionPanel';
import { SelectedItemsBody } from '@/components/panels/selectionPanel/selectedItemsBody';
import { SelectedItemsHeader } from '@/components/panels/selectionPanel/selectedItemsHeader';
import { ScrollableWrapper } from '@/components/scroll/scrollableContext';
import { ScrollableHeader } from '@/components/scroll/scrollableHeader';
import { useScrollable } from '@/components/scroll/useScrollable';
import { SearchBar } from '@/components/search/searchBar';
import { conditionValues, type MultiSelectConditionComponentProps } from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';
import { useDebouncedValue } from '@/utils/useDebouncedValue';
import { useResourceIdentifiers } from '../useResourceIentifiers';
export const ResourceNameCondition = props => {
  const $ = _c(4);
  if (props.condition.operator === ConditionOperatorEnum.In || props.condition.operator === ConditionOperatorEnum.NotIn) {
    let t0;
    if ($[0] !== props) {
      t0 = <ResourceNameSelection {...props} />;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== props) {
    t0 = <ResourceNameManualSelection {...props} />;
    $[2] = props;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
const ResourceNameSelection = props => {
  const $ = _c(7);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebouncedValue(searchText, 300);
  const {
    data: body,
    isLoading
  } = useResourceIdentifiers(props.supportedProviders, props.supportedResourceTypes, debouncedSearch || undefined);
  let t0;
  if ($[0] !== body?.resources) {
    t0 = Array.from(new Set((body?.resources || []).map(_temp))).sort().map(_temp2);
    $[0] = body?.resources;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const names = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setSearchText("");
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== isLoading || $[4] !== names || $[5] !== props) {
    t2 = <MultiSelectConditionPanel showAddNewButton={true} showSearchBox={true} isLoading={isLoading} entityName="name" options={names} onSearchChange={setSearchText} {...props} onPanelClose={t1} />;
    $[3] = isLoading;
    $[4] = names;
    $[5] = props;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
const ResourceNameManualSelection = (props: MultiSelectConditionComponentProps) => {
  const [valueFilter, setValueFilter] = useState('');
  const panel = useConditionPanel(props.panelHost, () => setValueFilter(''));
  const selectedValues = conditionValues(props.condition);
  const options = selectedValues.map(value => ({
    id: value,
    name: value
  }));
  return <>
      {!panel.isHosted && <ConditionPanelTriggerButton ref={panel.triggerRef} onClick={panel.open} options={options} selectedValues={selectedValues} />}
      <Popover open={panel.isOpen} anchorEl={panel.anchorEl} onClose={panel.close} anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'left'
    }} transformOrigin={{
      vertical: 'top',
      horizontal: 'left'
    }} slotProps={{
      paper: {
        sx: {
          marginTop: '4px'
        }
      }
    }}>
        <Stack className='w-[308px] max-h-[480px] overflow-hidden'>
          <Stack className='px-[24px] py-[12px]' sx={{}}>
            {panel.isHosted ? <Stack sx={{
            marginY: '12px'
          }}>
                <FilterOperatorSelect condition={props.condition} property={props.property} onChange={props.onChange} />
              </Stack> : <Typography className='my-[12px]' variant='body1'>
                {`Add one or more ${pluralize(props.property.displayName.toLowerCase())}`}
              </Typography>}
            <SearchBar hideSearchIcon value={valueFilter} setValue={setValueFilter} placeholder={capitalize(`${props.property?.policyRelation && OPERATORS[props.property.policyRelation]?.[props.condition.operator as ConditionOperatorEnum]?.label}...`)} selectedValues={selectedValues} onAddClick={value_0 => {
            props.onChange({
              ...props.condition,
              value: value_0
            });
          }} showAddButton={true} />
          </Stack>
          {selectedValues.length > 0 && <>
              <Divider />
              <Stack sx={{
            overflow: 'hidden',
            flexGrow: 1
          }}>
                <ScrollableWrapper>
                  <SelectedValuesContent selectedValues={selectedValues} onClear={() => props.onChange({
                ...props.condition,
                value: []
              })} onRemove={item => {
                props.onChange({
                  ...props.condition,
                  value: selectedValues.filter(i => i !== item)
                });
              }} />
                </ScrollableWrapper>
              </Stack>
            </>}
        </Stack>
      </Popover>
    </>;
};
const SelectedValuesContent = t0 => {
  const $ = _c(14);
  const {
    selectedValues,
    onClear,
    onRemove
  } = t0;
  const {
    setIsScrolledVertically
  } = useScrollable();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      "& .MuiTypography-root": {
        fontWeight: "bold"
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = selectedValues.length;
  let t3;
  if ($[1] !== selectedValues.length) {
    t3 = pluralize("value", selectedValues.length);
    $[1] = selectedValues.length;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = `${t2} ${t3} added`;
  let t5;
  if ($[3] !== onClear || $[4] !== t4) {
    t5 = <ScrollableHeader hideBorderWhenOnTop={true} className="py-[12px] px-[24px]" sx={t1}><SelectedItemsHeader title={t4} clearAll={onClear} /></ScrollableHeader>;
    $[3] = onClear;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Divider />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== onRemove || $[8] !== selectedValues || $[9] !== setIsScrolledVertically) {
    t7 = <SelectedItemsBody items={selectedValues} renderItem={_temp3} itemKey={_temp4} onRemove={onRemove} onScrollVerticallyChange={setIsScrolledVertically} />;
    $[7] = onRemove;
    $[8] = selectedValues;
    $[9] = setIsScrolledVertically;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5 || $[12] !== t7) {
    t8 = <>{t5}{t6}{t7}</>;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
};
function _temp(x) {
  return x.name;
}
function _temp2(x_0) {
  return {
    id: x_0,
    name: x_0
  };
}
function _temp3(item) {
  return item;
}
function _temp4(item_0) {
  return item_0;
}